/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- Step logic relies on evolving API/contracts; safe cleanup deferred. */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// CSV Upload Selection Modal Component
import {
  Alert,
  Badge,
  Button,
  CardHeader,
  Checkbox,
  DataCell,
  Dialog,
  EmptyContent,
  Flex,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import { DataList } from '@/components/ui/DataList';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCloudDataConnection,
  IconCloudUpload,
  IconDatabaseImport,
  IconFiles,
  IconSearch,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';

// Reuse existing components
import { DataTable } from '@/components/data-table';
import { FilePreviewModal } from '@/components/file-preview';

// Reuse existing validation
import {
  getCsvFiles,
  getSelectedCsvFileHeaders,
  validateCsvFile,
} from '@/services/csvFilesApi';

// Types
// Types
import {
  FileMetadata,
  FileSummaryStats,
  FileValidationResult,
  SelectedFile,
  StepComponentProps,
} from '../types';
import { parseCSVContent } from '@/components/file-preview/utils/csvParser';
import { ENABLE_EXTENDED_FILE_UPLOAD_FEATURES } from '@/config/constants';

// Components
import { AutoMappingToggle } from '../AutoMappingToggle';

// Hooks
import { useAuth } from '@/contexts/AuthContext';

// Local components
import { FileActions } from '../FileActions';
import { formatFileSize } from '@/utils/fileUtils.ts';
import {
  getCsvFileDownloadEndpoint,
  getCsvFileVersionDownloadEndpoint,
} from '@/utils/downloadEndpoints';
import { toast } from '@/utils/toast';
import { CsvFile } from '@/types/csvFiles';
import {
  getValidationStatusInfo,
  formatVersionNumber,
} from '@/utils/csvFileUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDateTimeViewMode } from '@/components/ui/DataCell/useDateTimeViewMode';

dayjs.extend(relativeTime);

// Local utilities
import { createSelectedFileFromHeaderData } from '../../utils/fileMetadataUtils';

// Local empty state component
const FileUploadEmptyState: React.FC<{
  onSelectFiles: () => void;
  onOpenCsvImporter: () => void;
  loadingCsvFiles: boolean;
}> = ({ onSelectFiles, onOpenCsvImporter, loadingCsvFiles }) => {
  return (
    <EmptyContent
      style={{ alignSelf: 'center', minHeight: '400px' }}
      title="Add CSV files to start importing"
      description={
        ENABLE_EXTENDED_FILE_UPLOAD_FEATURES
          ? 'Drag & drop CSV files here, or use the buttons below to select files from your computer or existing CSV files.'
          : 'Use the button below to select CSV files from the CSV Upload section.'
      }
      icon={<IconCloudUpload />}
      withIconBox
    >
      {ENABLE_EXTENDED_FILE_UPLOAD_FEATURES ? (
        <Flex gap="3" direction="row" align="center">
          <Button
            onClick={onOpenCsvImporter}
            startIcon={<IconCloudDataConnection />}
            disabled={loadingCsvFiles}
            variant="classic"
            color="primary"
          >
            {loadingCsvFiles ? 'Loading...' : 'Select from CSV Upload'}
          </Button>
          <Text size="xsmall" color="textSecondary" mx="2">
            or
          </Text>
          <Button
            onClick={onSelectFiles}
            startIcon={<IconUpload />}
            variant="classic"
            color="primary"
          >
            Select CSV Files
          </Button>
        </Flex>
      ) : (
        <Button
          onClick={onOpenCsvImporter}
          startIcon={<IconCloudDataConnection />}
          disabled={loadingCsvFiles}
          variant="classic"
          color="primary"
        >
          {loadingCsvFiles ? 'Loading...' : 'Select from CSV Upload'}
        </Button>
      )}
    </EmptyContent>
  );
};

type FileUploadStepProps = StepComponentProps;

export const FileUploadStep: React.FC<FileUploadStepProps> = ({
  wizardState,
  onUpdateState,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  currentStep,
  totalSteps,
  onResetWizard,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState<SelectedFile | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [csvImporterModalOpen, setCsvImporterModalOpen] = useState(false);
  const [loadingCsvFiles, setLoadingCsvFiles] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | undefined>(
    undefined
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user: profile } = useAuth();
  const { selectedFiles } = wizardState;
  const restoredFileIdsRef = useRef<Set<number>>(new Set());

  // Restore CSV files from localStorage - reload header data for csv-importer files
  useEffect(() => {
    if (selectedFiles.length === 0) {
      return;
    }

    const restoreCsvFiles = async () => {
      // Find files that need restoration (csv-importer files without headerData)
      const csvFilesToRestore = selectedFiles.filter(
        file =>
          file.source === 'csv-importer' &&
          file.csvFile?.id &&
          !restoredFileIdsRef.current.has(file.csvFile.id) &&
          (!file.headerData || !file.metadata.columns.length)
      );

      if (csvFilesToRestore.length === 0) {
        return;
      }

      const studyId = profile?.current_study?.id;
      if (!studyId) {
        return;
      }

      try {
        const fileIds = csvFilesToRestore
          .map(file => file.csvFile?.id)
          .filter((id): id is number => id !== undefined);

        if (fileIds.length === 0) {
          return;
        }

        // Get headers and sample data for files that need restoration
        const headersResponse = await getSelectedCsvFileHeaders(fileIds);

        const restoredFiles: SelectedFile[] = selectedFiles.map(file => {
          // Skip files that don't need restoration
          if (
            file.source !== 'csv-importer' ||
            !file.csvFile?.id ||
            restoredFileIdsRef.current.has(file.csvFile.id) ||
            (file.headerData && file.metadata.columns.length > 0)
          ) {
            return file;
          }

          // Find corresponding header data
          const headerData = headersResponse.data.find(
            h => h.csv_file_id === file.csvFile!.id
          );

          if (!headerData) {
            // Mark as restored even if failed to prevent retries
            restoredFileIdsRef.current.add(file.csvFile.id);
            // Keep file but mark as invalid if header data is missing
            return {
              ...file,
              validation: {
                isValid: false,
                errors: ['Failed to restore file metadata'],
                warnings: [],
              },
            };
          }

          // Restore file with header data
          const restoredFile = createSelectedFileFromHeaderData(
            {
              id: file.csvFile.id,
              filename: file.csvFile.filename,
              size: file.csvFile.size,
            },
            headerData
          );

          // Preserve existing csvFile data if available
          if (file.csvFile) {
            Object.assign(restoredFile.csvFile, file.csvFile);
          }

          // Mark as restored
          restoredFileIdsRef.current.add(file.csvFile.id);

          return restoredFile as unknown as SelectedFile;
        });

        // Update state with restored files
        onUpdateState({ selectedFiles: restoredFiles });
      } catch (error) {
        console.error('Failed to restore CSV files from localStorage:', error);
        // Mark files as restored even on error to prevent infinite retries
        csvFilesToRestore.forEach(file => {
          if (file.csvFile?.id) {
            restoredFileIdsRef.current.add(file.csvFile.id);
          }
        });
      }
    };

    restoreCsvFiles();
  }, [selectedFiles, profile?.current_study?.id, onUpdateState]);

  // Cleanup blob URL when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  // Cleanup blob URL when modal closes
  useEffect(() => {
    if (!previewModalOpen && previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(undefined);
    }
  }, [previewModalOpen, previewBlobUrl]);

  // Calculate summary statistics
  const summaryStats: FileSummaryStats = useMemo(() => {
    const stats = selectedFiles.reduce(
      (acc, file) => ({
        totalFiles: acc.totalFiles + 1,
        totalColumns: acc.totalColumns + file.metadata.columnCount,
        totalRows: acc.totalRows + file.metadata.rowCount,
        totalSize: acc.totalSize + file.metadata.size,
        validFiles: acc.validFiles + (file.validation.isValid ? 1 : 0),
        invalidFiles: acc.invalidFiles + (file.validation.isValid ? 0 : 1),
      }),
      {
        totalFiles: 0,
        totalColumns: 0,
        totalRows: 0,
        totalSize: 0,
        validFiles: 0,
        invalidFiles: 0,
      }
    );

    return {
      ...stats,
      status:
        stats.totalFiles === 0
          ? 'empty'
          : stats.invalidFiles > 0
            ? 'partial'
            : 'complete',
    };
  }, [selectedFiles]);

  // Process files and extract metadata
  const processFiles = useCallback(
    async (files: File[]): Promise<SelectedFile[]> => {
      const processedFiles: SelectedFile[] = [];

      for (const file of files) {
        try {
          // Validate file
          const csvValidation = validateCsvFile(file);
          const validation: FileValidationResult = {
            isValid: csvValidation.isValid,
            errors: csvValidation.error ? [csvValidation.error] : [],
            warnings: [],
          };

          let metadata: FileMetadata = {
            columnCount: 0,
            rowCount: 0,
            columns: [],
            sampleColumns: [],
            size: file.size,
          };

          // If valid, parse CSV to get metadata
          if (validation.isValid) {
            try {
              const text = await file.text();
              const csvData = parseCSVContent(text, 100); // Parse first 100 rows for metadata

              metadata = {
                columnCount: csvData.headers.length,
                rowCount: csvData.totalRows,
                columns: csvData.headers,
                sampleColumns: csvData.headers.slice(0, 10), // First 10 columns
                size: file.size,
              };
            } catch (parseError) {
              validation.isValid = false;
              validation.errors = ['Failed to parse CSV content'];
            }
          }

          processedFiles.push({
            file,
            source: 'uploaded',
            validation,
            metadata,
          });
        } catch (error) {
          // Handle any processing errors
          processedFiles.push({
            file,
            source: 'uploaded',
            validation: {
              isValid: false,
              errors: ['Failed to process file'],
              warnings: [],
            },
            metadata: {
              columnCount: 0,
              rowCount: 0,
              columns: [],
              sampleColumns: [],
              size: file.size,
            },
          });
        }
      }

      return processedFiles;
    },
    []
  );

  // Handle file selection
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setProcessingFiles(true);
      try {
        const processedFiles = await processFiles(files);
        const updatedFiles = [...selectedFiles, ...processedFiles];
        onUpdateState({ selectedFiles: updatedFiles });
      } catch (_error) {
        // Best-effort: errors are surfaced via UI state/toasts in helpers.
        void 0;
      } finally {
        setProcessingFiles(false);
      }
    },
    [selectedFiles, processFiles, onUpdateState]
  );

  // Handle CSV files selection from importer
  const handleCsvFilesSelected = useCallback(
    async (csvFiles: CsvFile[]) => {
      if (csvFiles.length === 0) return;

      setLoadingCsvFiles(true);
      try {
        // Get study ID from user profile
        const studyId = profile?.current_study?.id;
        if (!studyId) {
          throw new Error('No current study selected');
        }

        const processedFiles: SelectedFile[] = [];

        // Extract file IDs for API call
        const fileIds = csvFiles.map(csvFile => csvFile.id);

        // Get headers and sample data for selected files
        let headersResponse;
        try {
          headersResponse = await getSelectedCsvFileHeaders(fileIds);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to load file headers';
          toast.error('Failed to load file headers', {
            description:
              errorMessage ||
              'An error occurred while loading file metadata. Please try again.',
          });
          // Return early to prevent duplicate error messages
          return;
        }

        // Process each selected file
        for (const csvFile of csvFiles) {
          try {
            // Find corresponding header data
            const headerData = headersResponse.data.find(
              h => h.csv_file_id === csvFile.id
            );

            if (!headerData) {
              // Add file with basic metadata if header data is missing
              processedFiles.push({
                csvFile,
                source: 'csv-importer',
                validation: {
                  isValid: false,
                  errors: ['Failed to load file metadata'],
                  warnings: [],
                },
                metadata: {
                  columnCount: 0,
                  rowCount: 0,
                  columns: [],
                  sampleColumns: [],
                  size: csvFile.size,
                },
              });
              continue;
            }

            // Use version_for_import data if available, otherwise fallback to file data
            const fileSize = csvFile.version_for_import?.size || csvFile.size;
            const fileName = csvFile.version_for_import?.filename || csvFile.filename;

            // Create SelectedFile using header data
            const selectedFile = createSelectedFileFromHeaderData(
              {
                id: csvFile.id,
                filename: fileName,
                size: fileSize,
              },
              headerData
            );

            // Update csvFile with full data from the original CsvFile
            // This preserves version_for_import and other fields
            Object.assign(selectedFile.csvFile!, csvFile);
            
            // Update metadata size to match version_for_import if available
            if (csvFile.version_for_import?.size) {
              selectedFile.metadata.size = csvFile.version_for_import.size;
            }

            processedFiles.push(selectedFile as unknown as SelectedFile);
          } catch (error) {
            // Add file with basic metadata if processing fails
            processedFiles.push({
              csvFile,
              source: 'csv-importer',
              validation: {
                isValid: false,
                errors: ['Failed to load file metadata'],
                warnings: [],
              },
              metadata: {
                columnCount: 0,
                rowCount: 0,
                columns: [],
                sampleColumns: [],
                size: csvFile.size,
              },
            });
          }
        }

        const updatedFiles = [...selectedFiles, ...processedFiles];
        onUpdateState({ selectedFiles: updatedFiles });
        setCsvImporterModalOpen(false);

        if (processedFiles.length > 0) {
          toast.success(
            `Added ${processedFiles.length} CSV file(s) from importer`
          );
        }
      } catch (error) {
        toast.error('Failed to add CSV files', {
          description:
            'An error occurred while processing the selected files. Please try again.',
        });
      } finally {
        setLoadingCsvFiles(false);
      }
    },
    [selectedFiles, profile?.current_study?.id, onUpdateState]
  );

  // File input handlers
  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        handleFilesSelected(Array.from(event.target.files));
        event.target.value = '';
      }
    },
    [handleFilesSelected]
  );

  const handleSelectFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Drag and drop handlers
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFilesSelected(acceptedFiles);
    },
    [handleFilesSelected]
  );

  const dropzoneConfig = useMemo(
    () =>
      ENABLE_EXTENDED_FILE_UPLOAD_FEATURES
        ? {
            onDrop,
            accept: {
              'text/csv': ['.csv'],
            },
            multiple: true,
            noClick: true,
            onDragEnter: () => setDragActive(true),
            onDragLeave: () => setDragActive(false),
            onDropAccepted: () => setDragActive(false),
            onDropRejected: () => setDragActive(false),
          }
        : {
            onDrop: () => {},
            multiple: false,
            noClick: true,
            disabled: true,
          },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive } =
    useDropzone(dropzoneConfig);

  // File management handlers
  const handleRemoveFile = useCallback(
    (fileToRemove: SelectedFile) => {
      const updatedFiles = selectedFiles.filter(f => {
        if (f.source === 'uploaded' && fileToRemove.source === 'uploaded') {
          return f.file !== fileToRemove.file;
        } else if (
          f.source === 'csv-importer' &&
          fileToRemove.source === 'csv-importer'
        ) {
          return f.csvFile?.id !== fileToRemove.csvFile?.id;
        }
        return true;
      });
      onUpdateState({ selectedFiles: updatedFiles });
    },
    [selectedFiles, onUpdateState]
  );

  const handleClearAllFiles = useCallback(() => {
    // Clear all files and all mappings (same as Start New button)
    // Use onResetWizard if available to fully reset state and localStorage
    if (onResetWizard) {
      onResetWizard();
      toast.success('All files and mappings cleared');
    } else {
      // Fallback to onUpdateState if onResetWizard is not available
      onUpdateState({
        selectedFiles: [],
        fileToFormMappings: [],
        columnMappings: [],
        subformRelationships: [],
        validationResults: [],
        validationErrors: undefined,
        importMappingId: undefined,
        isValid: false,
      });
      toast.success('All files and mappings cleared');
    }
  }, [onUpdateState, onResetWizard]);

  const handlePreviewFile = useCallback((file: SelectedFile) => {
    setPreviewFile(file);

    // Create blob URL for uploaded files
    if (file.source === 'uploaded' && file.file) {
      const blobUrl = URL.createObjectURL(file.file);
      setPreviewBlobUrl(blobUrl);
    } else {
      setPreviewBlobUrl(undefined);
    }

    setPreviewModalOpen(true);
  }, []);

  const handlePreviewModalChange = useCallback(
    (open: boolean) => {
      setPreviewModalOpen(open);
      if (!open) {
        setPreviewFile(null);
        if (previewBlobUrl) {
          URL.revokeObjectURL(previewBlobUrl);
          setPreviewBlobUrl(undefined);
        }
      }
    },
    [previewBlobUrl]
  );

  // Table columns for file list (based on CsvFilesTable pattern)
  const enableColumnVisibility = true;
  const pageSizeOptions = useMemo(() => [10, 25, 50, 100], []);

  const tableColumns = useMemo(
    () => [
      {
        id: 'fileName',
        accessorFn: (row: SelectedFile) => {
          if (row.source === 'uploaded') {
            return row.file!.name;
          }
          // Use version_for_import filename if available, otherwise fallback to csvFile filename
          return (
            row.csvFile?.version_for_import?.filename ||
            row.csvFile!.filename
          );
        },
        header: 'File Name',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            mimeType: 'text/csv',
            truncate: true,
            endSlot: (row: SelectedFile) => {
              if (row.source !== 'csv-importer' || !row.csvFile) {
                return null;
              }
              
              const versionForImport = row.csvFile.version_for_import;
              if (!versionForImport) {
                return (
                  <Badge
                    size="xsmall"
                    color="gray"
                    variant="surface"
                    startIcon={<IconCloudDataConnection />}
                  >
                    Not ready
                  </Badge>
                );
              }
              
              const versionText = formatVersionNumber(
                versionForImport.order_index,
                versionForImport.id
              );
              
              return (
                <Badge
                  size="xsmall"
                  color="feature"
                  variant="surface"
                  startIcon={<IconCloudDataConnection />}
                >
                  {versionText}
                </Badge>
              );
            },
          },
          size: 'minmax(200px, 1fr)',
          minSize: 200,
          maxSize: '2fr',
          required: true,
        },
      },
      {
        accessorKey: 'metadata.size',
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          size: 100,
          minSize: 80,
          maxSize: 120,
        },
      },
      {
        accessorKey: 'metadata.columnCount',
        header: 'Columns',
        meta: {
          cell: {
            variant: 'count',
            size: 'small',
          },
          size: 80,
          minSize: 70,
          maxSize: 100,
        },
      },
      {
        accessorKey: 'metadata.sampleColumns',
        header: 'Sample Columns',
        meta: {
          cell: {
            variant: 'chips',
            maxVisible: 4,
            chipSize: 'xsmall',
            chipColor: 'default',
            chipVariant: 'surface',
          },
          size: 'minmax(150px, 250px)',
          minSize: 150,
          maxSize: '1fr',
        },
        enableSorting: false,
      },
      {
        id: 'validationStatus',
        accessorFn: (row: SelectedFile) =>
          row.validation.isValid ? 'valid' : 'invalid',
        header: 'Status',
        meta: {
          cell: {
            variant: 'status',
            entityType: 'csv_file',
            size: 'xsmall',
          },
          size: 100,
          minSize: 80,
          maxSize: 120,
        },
      },
      {
        id: 'actions',
        header: () => null,
        cell: ({ row }: any) => {
          const file = row.original as SelectedFile;
          return (
            <FileActions
              file={file}
              onPreview={handlePreviewFile}
              onRemove={handleRemoveFile}
            />
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          width: 'auto',
          systemColumn: true,
        },
      },
    ],
    [handlePreviewFile, handleRemoveFile]
  );

  const showDropOverlay =
    ENABLE_EXTENDED_FILE_UPLOAD_FEATURES && (dragActive || isDragActive);
  const hasFiles = selectedFiles.length > 0;
  const canProceed = summaryStats.validFiles > 0;

  // Handle CSV importer modal
  const handleOpenCsvImporter = useCallback(() => {
    setCsvImporterModalOpen(true);
  }, []);

  const handleCsvImporterModalChange = useCallback((open: boolean) => {
    setCsvImporterModalOpen(open);
  }, []);

  // Right slot for table toolbar (Add More Files button)
  const rightSlot = useMemo(
    () => (
      <>
        <Flex direction="column" gap="3" mr="4">
          <Flex gap="2" wrap="wrap">
            <Badge size="xsmall">
              Selected Files: {summaryStats.totalFiles}
            </Badge>
            <Badge size="xsmall">
              Total Columns:
              {summaryStats.totalColumns.toLocaleString()}
            </Badge>
            <Badge size="xsmall">
              Total Rows: {summaryStats.totalRows.toLocaleString()}
            </Badge>
            <Badge size="xsmall">
              Total Size: {formatFileSize(summaryStats.totalSize)}
            </Badge>
            <Flex direction="row" gap="1">
              <Text ml="2" size="2xsmall" highContrast>
                Status:
              </Text>
              <Badge size="xsmall" color="success" variant="surface">
                {summaryStats.validFiles} Valid
              </Badge>
              {summaryStats.invalidFiles > 0 && (
                <Badge size="xsmall" color="error" variant="surface">
                  {summaryStats.invalidFiles} Invalid
                </Badge>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex gap="2">
          {hasFiles && (
            <Button
              variant="surface"
              color="error"
              size="small"
              onClick={handleClearAllFiles}
              startIcon={<IconX />}
            >
              Clear All Files
            </Button>
          )}
          <Button
            variant={hasFiles ? 'classic' : 'outlined'}
            color="secondary"
            size="small"
            onClick={handleOpenCsvImporter}
            startIcon={<IconCloudDataConnection />}
            disabled={loadingCsvFiles}
          >
            {loadingCsvFiles ? 'Loading...' : 'Select from CSV Upload'}
          </Button>

          {ENABLE_EXTENDED_FILE_UPLOAD_FEATURES && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleSelectFiles}
              startIcon={<IconUpload />}
              disabled={processingFiles}
            >
              Add More Files
            </Button>
          )}
        </Flex>
      </>
    ),
    [
      handleSelectFiles,
      handleOpenCsvImporter,
      handleClearAllFiles,
      processingFiles,
      loadingCsvFiles,
      summaryStats,
      hasFiles,
    ]
  );

  return (
    <Flex
      {...getRootProps()}
      style={{
        position: 'relative',
        width: 'inherit',
        height: 'inherit',

        justifyContent: 'stretch',
      }}
    >
      <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      <Flex direction="column" gap="0" flexGrow="1" style={{ height: '100%' }}>
        {/* File List or Empty State */}
        <>
          <Flex direction="column" gap="4">
            {/* Step Title and Description */}
            <Flex justify="between" align="center">
              <CardHeader
                size="large"
                cardTitle="File Upload"
                description="  Select CSV files for import"
              />

              <AutoMappingToggle
                enabled={wizardState.autoMappingEnabled}
                onChange={enabled =>
                  onUpdateState({ autoMappingEnabled: enabled })
                }
              />
            </Flex>

            <VisuallyHidden>
              {/* Navigation Buttons */}
              <Flex justify="between" align="center">
                <Button
                  variant="outlined"
                  onClick={onPrevious}
                  disabled={!canGoPrevious}
                  startIcon={<IconArrowLeft />}
                >
                  Previous
                </Button>

                <Text size="2" color="gray">
                  Step {currentStep} of {totalSteps}
                </Text>

                <Button
                  onClick={onNext}
                  disabled={!canProceed}
                  endIcon={<IconArrowRight />}
                  variant="classic"
                  color="primary"
                >
                  Continue to Form Detection
                </Button>
              </Flex>
            </VisuallyHidden>
          </Flex>
          <DataTable
            data={selectedFiles}
            columns={tableColumns}
            enableSorting={true}
            enableFiltering={true}
            enablePagination={selectedFiles.length > 10}
            enableColumnVisibility={enableColumnVisibility}
            enableRowSelection={false}
            searchPlaceholder="Search files..."
            pageSizeOptions={pageSizeOptions}
            toolbarLeftSlot={null}
            toolbarRightSlot={rightSlot}
            emptyMessage="No files selected"
            emptyIcon={<IconSearch />}
            customEmptyContent={
              <FileUploadEmptyState
                onSelectFiles={handleSelectFiles}
                onOpenCsvImporter={handleOpenCsvImporter}
                loadingCsvFiles={loadingCsvFiles}
              />
            }
            getRowId={(row: SelectedFile) =>
              row.source === 'uploaded'
                ? row.file!.name
                : `csv-${row.csvFile!.id}`
            }
          />
        </>

        {/* Validation Errors */}
        {summaryStats.invalidFiles > 0 && (
          <Alert color="warning" title="Some files have validation errors">
            <Text size="2">
              {summaryStats.invalidFiles} file(s) failed validation. Only valid
              CSV files can be imported. Please check the file format and try
              again.
            </Text>
          </Alert>
        )}
      </Flex>

      {/* Drag Overlay */}
      {showDropOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--primary-a1)',
            backgroundImage:
              'linear-gradient(to bottom, var(--primary-a4), var(--primary-a1))',
            backdropFilter: 'blur(5px)',
            border: '1px dashed var(--primary-8)',
            borderRadius: 'var(--radius-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <EmptyContent
            title=" Drop CSV files here"
            icon={<IconCloudUpload />}
            withIconBox
            size="medium"
          />
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          apiEndpoint={
            previewFile.source === 'csv-importer' && previewFile.csvFile
              ? previewFile.csvFile.version_for_import?.id
                ? getCsvFileVersionDownloadEndpoint(
                    previewFile.csvFile.id,
                    previewFile.csvFile.version_for_import.id
                  )
                : getCsvFileDownloadEndpoint(previewFile.csvFile)
              : undefined
          }
          fileUrl={previewBlobUrl}
          filename={
            previewFile.source === 'uploaded'
              ? previewFile.file!.name
              : previewFile.csvFile!.filename
          }
          mimeType="text/csv"
          fileSize={previewFile.metadata.size}
          open={previewModalOpen}
          onOpenChange={handlePreviewModalChange}
          title={`Preview: ${
            previewFile.source === 'uploaded'
              ? previewFile.file!.name
              : previewFile.csvFile!.filename
          }`}
          showDownloadButton={false}
        />
      )}

      {/* CSV Upload Selection Modal */}
      <CsvImporterSelectionModal
        open={csvImporterModalOpen}
        onOpenChange={handleCsvImporterModalChange}
        onFilesSelected={handleCsvFilesSelected}
        loading={loadingCsvFiles}
      />
    </Flex>
  );
};

// Last imported version cell component for CSV files
const LastImportedCell: React.FC<{ file: CsvFile }> = ({ file }) => {
  const importedVersion = file.latest_imported_version;
  const { viewMode, setViewMode } = useDateTimeViewMode();
  const isRelative = viewMode === 'relative';

  const formattedDate = useMemo(() => {
    if (!importedVersion?.imported_at) {
      return null;
    }
    const date = dayjs(importedVersion.imported_at);
    if (!date.isValid()) {
      return 'Invalid date';
    }
    if (isRelative) {
      return date.fromNow();
    } else {
      return date.format('MMM D, YYYY h:mm A');
    }
  }, [importedVersion?.imported_at, isRelative]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newMode = isRelative ? 'absolute' : 'relative';
    setViewMode(newMode);
  };

  if (!importedVersion?.imported_at) {
    return (
      <Text size="2" color="gray">
        -
      </Text>
    );
  }

  const versionNumber = formatVersionNumber(
    importedVersion.order_index,
    importedVersion.id
  );

  const importedCell = (
    <Flex align="center" gap="2" style={{ display: 'inline-flex' }}>
      <IconDatabaseImport size={16} style={{ color: 'var(--green-9)' }} />
      <Text size="2" color="gray" weight="medium">
        {versionNumber}
      </Text>
      <Text
        size="2"
        color="gray"
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
          userSelect: 'none',
        }}
        onClick={handleClick}
      >
        {formattedDate}
      </Text>
    </Flex>
  );

  return (
    <HoverCardRoot>
      <HoverCardTrigger>
        <div style={{ display: 'inline-flex' }}>{importedCell}</div>
      </HoverCardTrigger>
      <HoverCardContent size="2">
        <Flex direction="column" gap="3">
          <DataList.Root
            style={{
              gap: 'var(--space-4) var(--space-2)',
            }}
          >
            <DataList.Item>
              <DataList.Label>Version</DataList.Label>
              <DataList.Value>{versionNumber}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Imported</DataList.Label>
              <DataList.Value>
                <DataCell type="datetime" value={importedVersion.imported_at} />
              </DataList.Value>
            </DataList.Item>
            {importedVersion.imported_by && (
              <DataList.Item>
                <DataList.Label>Imported By</DataList.Label>
                <DataList.Value>
                  <DataCell type="user" value={importedVersion.imported_by} />
                </DataList.Value>
              </DataList.Item>
            )}
          </DataList.Root>
        </Flex>
      </HoverCardContent>
    </HoverCardRoot>
  );
};

// Approved cell component for CSV files
const ApprovedCell: React.FC<{ file: CsvFile }> = ({ file }) => {
  const statusInfo = getValidationStatusInfo(file);
  const { viewMode, setViewMode } = useDateTimeViewMode();
  const isRelative = viewMode === 'relative';

  const formattedDate = useMemo(() => {
    if (!statusInfo || statusInfo.status !== 'approved' || !statusInfo.changedAt) {
      return null;
    }
    const date = dayjs(statusInfo.changedAt);
    if (!date.isValid()) {
      return 'Invalid date';
    }
    if (isRelative) {
      return date.fromNow();
    } else {
      return date.format('MMM D, YYYY h:mm A');
    }
  }, [statusInfo, isRelative]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newMode = isRelative ? 'absolute' : 'relative';
    setViewMode(newMode);
  };

  if (!statusInfo || statusInfo.status !== 'approved' || !formattedDate) {
    return (
      <Text size="2" color="gray">
        -
      </Text>
    );
  }

  const approvedCell = (
    <Text
      size="2"
      color="gray"
      style={{
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textUnderlineOffset: '2px',
        userSelect: 'none',
      }}
      onClick={handleClick}
    >
      {formattedDate}
    </Text>
  );

  if (!statusInfo.changedBy) {
    return approvedCell;
  }

  return (
    <HoverCardRoot>
      <HoverCardTrigger>
        <div style={{ display: 'inline-flex' }}>{approvedCell}</div>
      </HoverCardTrigger>
      <HoverCardContent size="2">
        <Flex direction="column" gap="3">
          <DataList.Root
            style={{
              gap: 'var(--space-4) var(--space-2)',
            }}
          >
            <DataList.Item>
              <DataList.Label>Approved</DataList.Label>
              <DataList.Value>
                <DataCell type="datetime" value={statusInfo.changedAt} />
              </DataList.Value>
            </DataList.Item>
            {statusInfo.changedBy && (
              <DataList.Item>
                <DataList.Label>Approved By</DataList.Label>
                <DataList.Value>
                  <DataCell type="user" value={statusInfo.changedBy} />
                </DataList.Value>
              </DataList.Item>
            )}
          </DataList.Root>
        </Flex>
      </HoverCardContent>
    </HoverCardRoot>
  );
};

interface CsvImporterSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilesSelected: (files: CsvFile[]) => void;
  loading: boolean;
}

const CsvImporterSelectionModal: React.FC<CsvImporterSelectionModalProps> = ({
  open,
  onOpenChange,
  onFilesSelected,
  loading,
}) => {
  const { user: profile } = useAuth();
  const [csvFiles, setCsvFiles] = useState<CsvFile[]>([]);
  const [selectedCsvFiles, setSelectedCsvFiles] = useState<CsvFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const isLoadingRef = useRef(false);
  const [fileHeaders, setFileHeaders] = useState<
    Record<number, { columns: number; rows: number }>
  >({});
  const [pagination, setPagination] = useState<{
    current_page: number;
    next_page: number | null;
    prev_page: number | null;
    total_pages: number;
    total_count: number;
    per_page: number;
  } | null>(null);
  const [paginationState, setPaginationState] = useState<{
    pageIndex: number;
    pageSize: number;
  }>({ pageIndex: 0, pageSize: 10 });

  // Check if all selectable files on current page are selected
  const selectableFiles = useMemo(() => {
    return csvFiles.filter(file => file.version_for_import != null);
  }, [csvFiles]);

  const areAllPageFilesSelected = useMemo(() => {
    if (selectableFiles.length === 0) return false;
    return selectableFiles.every(file =>
      selectedCsvFiles.some(selected => selected.id === file.id)
    );
  }, [selectableFiles, selectedCsvFiles]);

  // Handle select/deselect all selectable files on current page
  const handleSelectAllPageFiles = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Add all selectable files from current page that are not already selected
        const newSelections = selectableFiles.filter(
          file => !selectedCsvFiles.some(selected => selected.id === file.id)
        );
        setSelectedCsvFiles(prev => [...prev, ...newSelections]);
      } else {
        // Remove all files from current page
        const currentPageFileIds = new Set(csvFiles.map(f => f.id));
        setSelectedCsvFiles(prev =>
          prev.filter(file => !currentPageFileIds.has(file.id))
        );
      }
    },
    [csvFiles, selectableFiles, selectedCsvFiles]
  );

  // Table columns for CSV file selection
  const tableColumns = useMemo(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={areAllPageFilesSelected}
            onCheckedChange={handleSelectAllPageFiles}
            size="small"
            title={
              areAllPageFilesSelected
                ? 'Deselect all files on this page'
                : 'Select all files on this page'
            }
          />
        ),
        cell: ({ row }: any) => {
          const file = row.original as CsvFile;
          const isSelected = selectedCsvFiles.some(f => f.id === file.id);
          const isSelectable = file.version_for_import != null;
          return (
            <div
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <Checkbox
                checked={isSelected}
                disabled={!isSelectable}
                onCheckedChange={checked => {
                  if (!isSelectable) return;
                  setSelectedCsvFiles(prev => {
                    if (checked) {
                      return [...prev, file];
                    } else {
                      return prev.filter(f => f.id !== file.id);
                    }
                  });
                }}
                size="small"
              />
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
        meta: {
          size: 40,
          minSize: 40,
          maxSize: 40,
          systemColumn: true,
        },
      },
      {
        id: 'filename',
        accessorFn: (row: CsvFile) => {
          return row.version_for_import?.filename || row.filename;
        },
        header: 'File Name',
        meta: {
          cell: {
            variant: 'fileName',
            showIcon: true,
            mimeType: 'text/csv',
            endSlot: (row: CsvFile) => {
              // Show version badge from version_for_import.order_index
              if (row.version_for_import) {
                return (
                  <Badge size="xsmall" color="feature" variant="surface">
                    v.{row.version_for_import.order_index}
                  </Badge>
                );
              }
              return (
                <Badge size="xsmall" color="gray" variant="surface">
                  Not ready
                </Badge>
              );
            },
          },
          size: 'minmax(200px, 1fr)',
          minSize: 200,
          maxSize: '1fr',
          required: true,
        },
      },
      {
        id: 'size',
        accessorFn: (row: CsvFile) => {
          return row.version_for_import?.size || row.size;
        },
        header: 'Size',
        meta: {
          cell: {
            variant: 'fileSize',
          },
          size: 100,
          minSize: 80,
          maxSize: 120,
        },
      },
      {
        id: 'columns',
        accessorFn: (row: CsvFile) => {
          const headerInfo = fileHeaders[row.id];
          return headerInfo?.columns || null;
        },
        header: 'Columns',
        meta: {
          cell: {
            variant: 'count',
            size: 'small',
          },
          size: 80,
          minSize: 70,
          maxSize: 100,
        },
      },
      {
        id: 'uploaded_by',
        accessorFn: (row: CsvFile) => {
          return row.version_for_import?.created_by || row.user;
        },
        header: 'Uploaded By',
        meta: {
          cell: {
            variant: 'user',
            showEmail: false,
          },
          size: 180,
          minSize: 150,
          maxSize: 250,
        },
      },
      {
        id: 'uploaded_at',
        accessorFn: (row: CsvFile) => {
          return row.version_for_import?.created_at || row.created_at;
        },
        header: 'Uploaded',
        meta: {
          cell: {
            variant: 'datetime',
            showTime: true,
          },
          size: 180,
          minSize: 150,
          maxSize: 220,
        },
      },
      {
        id: 'approved',
        accessorFn: (row: CsvFile) => {
          // Use last_version for approval info (as per user choice)
          const statusInfo = getValidationStatusInfo(row);
          return statusInfo?.changedAt || null;
        },
        header: 'Approved',
        cell: ({ row }: any) => {
          const file = row.original as CsvFile;
          return <ApprovedCell file={file} />;
        },
        meta: {
          size: 180,
          minSize: 150,
          maxSize: 220,
        },
      },
      {
        id: 'last_imported',
        accessorFn: (row: CsvFile) => {
          return row.latest_imported_version?.imported_at || null;
        },
        header: 'Last Imported',
        cell: ({ row }: any) => {
          const file = row.original as CsvFile;
          return <LastImportedCell file={file} />;
        },
        meta: {
          size: 200,
          minSize: 180,
          maxSize: 250,
        },
      },
    ],
    [
      selectedCsvFiles,
      fileHeaders,
      areAllPageFilesSelected,
      handleSelectAllPageFiles,
    ]
  );

  const loadCsvFiles = useCallback(
    async (page: number = 1, perPage: number = 10) => {
      if (!profile?.current_study?.id || isLoadingRef.current) return;

      isLoadingRef.current = true;
      setLoadingFiles(true);
      setLoadError(null);
      try {
        const response = await getCsvFiles({
          page,
          per_page: perPage,
          ready_for_import: true, // Only show files ready for import
        });
        // Use API data as-is (no mock versions)
        setCsvFiles(response.data);
        setPagination(response.pagination);
        setLoadError(null);

        // Load headers for all files on the current page
        if (response.data.length > 0) {
          const fileIds = response.data.map(file => file.id);
          try {
            const headersResponse = await getSelectedCsvFileHeaders(fileIds);

            const headersMap: Record<
              number,
              { columns: number; rows: number }
            > = {};
            headersResponse.data.forEach(header => {
              headersMap[header.csv_file_id] = {
                columns: header.headers.length,
                rows: header.first_10_rows.length,
              };
            });
            setFileHeaders(headersMap);
          } catch (error) {
            console.error('Error loading headers:', error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : 'Failed to load file headers';
            toast.error('Failed to load file headers', {
              description:
                errorMessage ||
                'An error occurred while loading file metadata. Please try again.',
            });
            setFileHeaders({});
          }
        } else {
          setFileHeaders({});
        }
      } catch (error) {
        console.error('Error loading CSV files:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load CSV files';
        toast.error('Failed to load CSV files', {
          description:
            errorMessage ||
            'An error occurred while loading files. Please try again.',
        });
        setCsvFiles([]);
        setLoadError(error instanceof Error ? error : new Error('Unknown error'));
      } finally {
        setLoadingFiles(false);
        isLoadingRef.current = false;
      }
    },
    [profile?.current_study?.id]
  );

  // Load initial data when modal opens
  useEffect(() => {
    if (open && profile?.current_study?.id && csvFiles.length === 0 && !loadError) {
      loadCsvFiles(1, 10);
    }
  }, [open, profile?.current_study?.id, csvFiles.length, loadCsvFiles, loadError]);

  const handlePaginationChange = useCallback(
    (
      updater:
        | { pageIndex: number; pageSize: number }
        | ((prev: { pageIndex: number; pageSize: number }) => {
            pageIndex: number;
            pageSize: number;
          })
    ) => {
      let newPagination: { pageIndex: number; pageSize: number };
      if (typeof updater === 'function') {
        newPagination = updater(paginationState);
      } else {
        newPagination = updater;
      }

      // Only update if pagination actually changed
      if (
        newPagination.pageIndex !== paginationState.pageIndex ||
        newPagination.pageSize !== paginationState.pageSize
      ) {
        setPaginationState(newPagination);
        loadCsvFiles(newPagination.pageIndex + 1, newPagination.pageSize);
      }
    },
    [paginationState, loadCsvFiles]
  );

  const handleConfirmSelection = () => {
    // Filter out files without version_for_import (should not happen, but safety check)
    const validFiles = selectedCsvFiles.filter(
      file => file.version_for_import != null
    );
    onFilesSelected(validFiles);
    setSelectedCsvFiles([]);
  };

  const handleClose = () => {
    onOpenChange(false);
    setSelectedCsvFiles([]);
    setPaginationState({ pageIndex: 0, pageSize: 10 });
    setPagination(null);
    setCsvFiles([]);
    setFileHeaders({});
    setLoadError(null);
  };

  const handleRowClick = (row: any) => {
    const file = row.original as CsvFile;
    // Don't allow selection of files without version_for_import
    if (!file.version_for_import) {
      return;
    }
    setSelectedCsvFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="expand">
        <Dialog.Header
          title="Select CSV Files for Importer"
          subtitle="Choose CSV files that were previously uploaded to the CSV Upload to use in your study data import."
          endSlot={
            <Dialog.Close>
              <Button variant="ghost" isIconOnly>
                <IconX />
              </Button>
            </Dialog.Close>
          }
        />

        <Dialog.Body>
          <DataTable
            data={csvFiles}
            columns={tableColumns}
            loading={loadingFiles}
            showSkeletonOnLoading={true}
            skeletonRows={paginationState.pageSize}
            enableSorting={true}
            enableFiltering={true}
            enablePagination={true}
            manualPagination={true}
            enableColumnVisibility={false}
            enableRowSelection={true}
            onRowClick={handleRowClick}
            searchPlaceholder="Search CSV files..."
            pageSizeOptions={[10, 25, 50, 100]}
            emptyMessage="No CSV files found"
            emptyIcon={<IconFiles />}
            getRowId={(row: CsvFile) => row.id.toString()}
            pagination={{
              pageSize: paginationState.pageSize,
              pageIndex: paginationState.pageIndex,
              pageCount: pagination?.total_pages || 0,
              totalCount: pagination?.total_count || 0,
              onPaginationChange: handlePaginationChange,
            }}
            toolbarRightSlot={
              <Text size="2" color="gray">
                {selectedCsvFiles.length} file(s) selected
              </Text>
            }
          />
        </Dialog.Body>

        <Dialog.Footer align="between">
          <Text size="2" color="gray">
            {selectedCsvFiles.length} file(s) selected
          </Text>
          <Flex gap="2">
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="classic"
              color="primary"
              onClick={handleConfirmSelection}
              disabled={selectedCsvFiles.length === 0 || loading}
              loading={loading}
            >
              {loading ? 'Adding Files...' : 'Add Selected Files'}
            </Button>
          </Flex>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default FileUploadStep;
